var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import TradingViewChartPanelTemplate from '@front/templates/TradingViewChartPanelTemplate.html';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { CurrentLang, Resources } from '@shared/localizations/Resources';
import { DataCache } from '@shared/commons/DataCache';
import { Periods } from '@shared/utils/History/TFInfo';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { LinkedSystem } from '../misc/LinkedSystem';
import { DynProperty } from '@shared/commons/DynProperty';
import { TvDataFeed } from '@shared/commons/TradingViewDataFeed/TvDataFeed';
import { TvInteriorIdCache, TvInteriorIdCacheMode } from '@shared/commons/TradingViewDataFeed/Caches/TvInteriorIdCache';
import { Instrument } from '@shared/commons/cache/Instrument';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { DockSystemInstance } from '../DockSystem';
import { TvHistoryResolutionHelper } from '@shared/commons/TradingViewDataFeed/Helpers/TvHistoryResolutionHelper';
import { TvHistoryAggregationsHelper } from '@shared/commons/TradingViewDataFeed/Helpers/TvHistoryAggregationsHelper';
import { TvSettingsAdapter } from '@shared/commons/TradingViewDataFeed/Settings/TvSettingsAdapter';
import { ThemeManager } from '../misc/ThemeManager';
import { Themes } from '@shared/utils/ThemesEnum';
const TRADINGVIEW_WIDGET_CONTAINER_ID_PREFIX = '-TV-'; // Reference to an attribute of a DOM element
const CURRENT_TIME_ZONE_PASSED_TO_TRADINGVIEW = 'Europe/Bucharest';
const DEFAULT_PERIOD = Periods.DAY;
export class TradingViewChartPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.tvWidget = null;
        this.language = 'en';
        this.theme = 'dark';
        this.tVWChartReady = false;
        this.onTvFeedReadyCount = 0;
        this.isMousePressedOutside = false;
        this.isMouseInsideChart = false;
        this.skipApplyInstrument = false;
        this.isNeedDeleteProps = false;
        this.tvSettingsAdapter = new TvSettingsAdapter();
        this.Name = 'TradingViewChartPanel';
        this.headerLocaleKey = 'panel.tradingViewChart.header';
        this.NeedCalculateRowCount = false;
        this.tvWidget = null; // TradingView Widget Holder
        this.language = 'en';
        this.model = null; // TerceraChartMVCModel instance
        this.cashItemSeriesSettings = null; // TerceraChartCashItemSeriesSettings instance
        this.currentBarWithMaxTime = null;
    }
    getType() {
        return PanelNames.TradingViewChartPanel;
    }
    oninit() {
        super.oninit();
        TvInteriorIdCache.mode = TvInteriorIdCacheMode.TvChart;
        this.onUndoRedoStateChanged = this.onUndoRedoStateChanged.bind(this);
        this.onTvChartMouseDown = this.onTvChartMouseDown.bind(this);
        this.onTvChartMouseUp = this.onTvChartMouseUp.bind(this);
        this.onTvChartMouseEnter = this.onTvChartMouseEnter.bind(this);
        this.onTvChartMouseLeave = this.onTvChartMouseLeave.bind(this);
        super.on('onTvChartMouseEnter', this.onTvChartMouseEnter);
        super.on('onTvChartMouseLeave', this.onTvChartMouseLeave);
        document.addEventListener('mousedown', this.handleGlobalMouseDown.bind(this));
        document.addEventListener('mouseup', this.handleGlobalMouseUp.bind(this));
    }
    oncomplete() {
        super.oncomplete();
        this.tvDataFeed = new TvDataFeed();
        this.tvDataFeed.onReadyEvent.Subscribe(this.onReadyEventCalled, this);
        super.observe('account', this.onAccountItemChanged);
        this.theme = this.getTvTheme();
        this.deserializeDelayedData();
        this.initTradingViewWidget();
        super.observe('instrumentItem', this.onInstrumentItemChanged);
        // super.observe('width', (nW) => { this.Controls.ToolsContainer.set('panelWidth', nW); });
        DockSystemInstance.dockRebuildEvent.Subscribe(this.callbackBeforeResize, this);
    }
    onteardown() {
        this.tvDataFeed.onReadyEvent.UnSubscribe(this.onReadyEventCalled, this);
        this.tvDataFeed.destroy();
        document.removeEventListener('mousedown', this.handleGlobalMouseDown);
        document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    }
    callbackBeforeResize() {
        var _a;
        if (!this.tVWChartReady) {
            return;
        }
        (_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.save((obj) => {
            this.tvPropsBeforeResize = obj;
        });
    }
    initTradingViewWidget() {
        this.tVWChartReady = false;
        if (this.tvWidget != null) {
            this.deleteTradingViewWidget();
        }
        TvHistoryAggregationsHelper.parseKeys();
        const defaultResoultion = TvHistoryResolutionHelper.convertPeriodToResolution(DEFAULT_PERIOD);
        const enabled_features = [
            // 'clear_bars_on_series_error',
            'show_symbol_logos',
            'show_symbol_logo_in_legend',
            'hide_resolution_in_legend',
            'pre_post_market_sessions'
        ];
        if (TvHistoryAggregationsHelper.hasTicks()) {
            enabled_features.push('tick_resolution');
        }
        if (TvHistoryAggregationsHelper.hasSeconds()) {
            enabled_features.push('seconds_resolution');
        }
        if (TvHistoryAggregationsHelper.isCustomAggregationsAllowed()) {
            enabled_features.push('custom_resolutions');
        }
        this.tvWidget = new TradingView.widget({
            symbol: this.getTVSymbol(), // Default symbol pair
            locale: this.language,
            timezone: CURRENT_TIME_ZONE_PASSED_TO_TRADINGVIEW,
            autosize: true, // A Boolean value showing whether the chart should use all the available space in the container and resize when the container itself is resized.
            container: this.getWidgetContainerID(), // Reference to an attribute of a DOM element
            datafeed: this.tvDataFeed,
            debug: false,
            enabled_features,
            disabled_features: [
                /* 'widget_logo', */
                'timeframes_toolbar',
                'use_localstorage_for_settings',
                'adaptive_logo',
                'symbol_search_hot_key'
            ], //, 'symbol_info'],//, 'header_symbol_search', 'header_compare', 'header_interval_dialog_button'],
            // timeframe: '1M',                                        // Selected data timeframe
            interval: defaultResoultion,
            // supported_resolutions: TvChartSupportedResolutionArray,
            custom_font_family: 'Trebuchet MS',
            library_path: '../../scripts/Libs/TradingViewChart/charting_library/',
            // context_menu: {     // hide after anal's comment https://tp.traderevolution.com/entity/117411
            //     items_processor: (items, actionsFactory) =>
            //     {
            //         this.setFocus();// for accWidget and lookupDropdown form close when click on iframe
            //         return TradingViewUtils.addChangeThemeContextMenuItem(this.theme, items, actionsFactory, this.onChangeThemeContextMenuClick.bind(this));
            //     },
            // },
            custom_css_url: 'current_theme/tvchart.css',
            theme: this.theme,
            // custom_css_url: '../../css/custom_style.css',        // blob:http://192.168.10.52:5055/6cebf6e1-505d-4909-9f82-3dfa7f9f998f:1 Refused to apply style from 'http://192.168.10.52:5055/scripts/Libs/TradingViewChart/css/custom_style.css' because its MIME type ('text/html') is not a supported stylesheet MIME type, and strict MIME checking is enabled.
            // fullscreen: false,                                   // A Boolean value showing whether the chart should use all the available space in the window.
            studies_access: {
                type: 'black',
                tools: [
                    {
                        name: 'Correlation - Log'
                        // grayed: true
                    },
                    {
                        name: 'Correlation Coefficient'
                    },
                    {
                        name: 'Spread'
                    },
                    {
                        name: 'Ratio'
                    }
                ]
            },
            settings_adapter: this.tvSettingsAdapter
        });
        this.tvWidget.onChartReady(() => {
            this.applyOverrides();
            this.onTVWChartReady();
        });
    }
    onTVWChartReady() {
        // BrowserUtils.log({ text: 'onChartReady started ', color: 'gray' });
        const tvW = this.tvWidget;
        try {
            if (isNullOrUndefined(tvW.activeChart()))
                return;
        }
        catch (e) {
            return;
        }
        this.setWatermarkVisibility();
        // Отключить торговлю и алерты на TV чарте:
        // this.Controls.ToolsContainer.set('instrument', this.GetInstrument());
        this.subscribeTvWidgetEvents();
        if (tvW != null) {
            tvW.changeTheme(this.theme);
            if (this.tvWidgetDefferedProps != null) {
                tvW.load(this.tvWidgetDefferedProps);
                this.tvWidgetDefferedProps = null;
            }
            if (this.tvPropsBeforeResize != null) {
                tvW.load(this.tvPropsBeforeResize);
                if (this.isNeedDeleteProps) {
                    this.tvPropsBeforeResize = null;
                }
            }
        }
        this.tVWChartReady = true;
        this.ApplySymbolForTvWidget();
        this.updatePanelHeader();
        // BrowserUtils.log({ text: 'chartIsReady', color: '#74c489' });
    }
    onInstrumentItemChanged(newValue, oldValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.skipApplyInstrument) {
                this.skipApplyInstrument = false;
                return;
            }
            if (newValue === oldValue) {
                return;
            }
            let instrument = newValue;
            if (instrument === null || instrument === void 0 ? void 0 : instrument.isFakeNonFixed()) {
                instrument = yield DataCache.getInstrumentByInstrumentTradableID_NFL(instrument.InstrumentTradableID, instrument.Route, instrument.GetInteriorID());
            }
            if (instrument != null) {
                this.updatePanelHeader();
                this.ApplySymbolForTvWidget();
                this.symbolLink_Out(false, instrument);
            }
        });
    }
    GetInstrument() {
        return this.get('instrumentItem');
    }
    SetInstrument(instrument) {
        void this.set('instrumentItem', instrument);
    }
    onAccountItemChanged() {
        var _a;
        const account = (_a = this.get('account')) !== null && _a !== void 0 ? _a : DataCache.MainAccountNew;
        this.tvDataFeed.setCurrentAccount(account);
        if (this.tvWidget != null && this.tVWChartReady) {
            this.tvWidget.activeChart().resetData();
        }
    }
    onReadyEventCalled() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.onTvFeedReadyCount++;
            // когда другая панель перекрыает чарт, то он перегружается
            // и при этом onChartReady не срабатывает повторно
            // понять что чарт перегрузился мы можем только по этому событию
            if (this.onTvFeedReadyCount === 1) {
                return;
            }
            const panel = this.find('*');
            this.isNeedDeleteProps = panel.offsetWidth !== 0;
            // и нужно подождать пока чарт не прогрузится
            let isTvChartReady = false;
            while (!isTvChartReady) {
                try {
                    if (((_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.activeChart()) != null) {
                        isTvChartReady = true;
                    }
                }
                catch (_b) {
                    isTvChartReady = false;
                }
                yield new Promise((resolve) => setTimeout(resolve, 100));
            }
            this.onTVWChartReady();
        });
    }
    ApplySymbolForTvWidget() {
        var _a;
        if (this.tVWChartReady) {
            const symbolName = this.getTVSymbol();
            (_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.setSymbol(symbolName, this.getTvResoultion(), () => {
                // BrowserUtils.log({ text: 'widget setSymbol ' + this.getTVSymbol() + ' ' + DataCache.Instruments[this.getTVSymbol()] })
            });
        }
    }
    // onMouseMove (event) {
    //     this.Controls.ToolsContainer.onMouseMove(event);
    // }
    // onMouseLeave (event) {
    //     this.Controls.ToolsContainer.onMouseUp(event); // ??
    // }
    updatePanelHeader() {
        var _a;
        if (!this.tVWChartReady) {
            return;
        }
        const activeChart = (_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.activeChart();
        if (activeChart == null) {
            return;
        }
        let header = Resources.getResource(this.headerLocaleKey) + ' ';
        const ins = this.GetInstrument();
        if (Instrument.IsWorkingInstrument(ins))
            header += ins.DisplayName() + ' ';
        const resolution = this.getTvResoultion();
        const period = TvHistoryResolutionHelper.convertResolutionToPeriod(resolution);
        header +=
            Resources.getResource('chart.timeframeselector.time') + ' - ' + Periods.ToLocalizedShortPeriod(period);
        void this.set('header', header);
    }
    // onChangeThemeContextMenuClick(subitem)
    // {
    //     if (subitem && this.tvWidget) {
    //         this.theme = subitem.getLabel().toLowerCase();
    //         this.tvWidget.changeTheme(this.theme);
    //     }
    // }
    localize() {
        super.localize();
        this.changeTradingViewWidgetLanguage();
    }
    changeTradingViewWidgetLanguage() {
        if (this.language === CurrentLang) {
            return;
        }
        // BrowserUtils.log({ text: 'entered. Current language = ' + this.language + ' in panel ' + this.getWidgetContainerID() + '\n trying to change to new language ' + CurrentLang, color: '#8439ed' });
        const oldLang = this.language;
        this.language = CurrentLang.split('_')[0]; // convert language from Resources (e.g. Chinese) to TradingView language format
        if (oldLang !== this.language && !isNullOrUndefined(this.tvDataFeed)) {
            this.initTradingViewWidget();
        } // Create a new widget with the updated language
    }
    // TickAsync () {
    //     if (!super.get('visible')) { return; }
    //     this.Controls.ToolsContainer.TickAsync();
    // }
    symbolLink_In(symbolInteriorID) {
        const newInstr = DataCache.getInstrumentByName(symbolInteriorID);
        const oldIns = this.GetInstrument();
        if (newInstr != null && (oldIns === null || oldIns === void 0 ? void 0 : oldIns.GetInteriorID()) !== symbolInteriorID) {
            this.SetInstrument(newInstr);
        }
    }
    symbolLink_Out(newSubscriber, instrument) {
        if (instrument == null) {
            const ins = this.GetInstrument();
            if (ins == null)
                return;
            instrument = ins;
        }
        const color = super.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    dispose() {
        this.tvDataFeed.onReadyEvent.UnSubscribe(this.onReadyEventCalled, this);
        DockSystemInstance.dockChangedEvent.UnSubscribe(this.callbackBeforeResize, this);
        this.deleteTradingViewWidget();
        // this.setFocusOnIframeEventSubscriptions(false);
        super.dispose();
        // BrowserUtils.log({ color: '#2deb7c', background: '#ed1a2f' });
    }
    deleteTradingViewWidget() {
        var _a;
        this.tVWChartReady = false;
        this.unsubscribeTvWidgetEvents();
        (_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.remove();
        const container = document.getElementById(this.getWidgetContainerID()); // Get the container element
        while ((container === null || container === void 0 ? void 0 : container.firstChild) != null) {
            // Remove the existing widget
            container.removeChild(container.firstChild);
        }
        this.tvWidget = null;
        // BrowserUtils.log({ text: 'this.tvWidget was removed', color: 'gray', background: '#800f20' });
    }
    getTVSymbol() {
        const ins = this.GetInstrument();
        if (ins == null) {
            return null;
        }
        const ticker = ins.GetInteriorID(); // ins.DisplayName();
        return ticker;
    }
    themeChange() {
        super.themeChange();
        this.theme = this.getTvTheme();
        if (!isNullOrUndefined(this.tvWidget) && this.tVWChartReady) {
            void this.tvWidget.changeTheme(this.theme).then(() => {
                this.applyOverrides();
            });
        }
    }
    getTvResoultion() {
        var _a;
        const activeChart = (_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.activeChart();
        if (activeChart == null) {
            return TvHistoryResolutionHelper.convertPeriodToResolution(DEFAULT_PERIOD);
        }
        return activeChart.resolution();
    }
    getWidgetContainerID() {
        let containerID = this.get('widgetContainerID');
        if (containerID == null) {
            containerID = TRADINGVIEW_WIDGET_CONTAINER_ID_PREFIX + this._guid;
            void this.set('widgetContainerID', containerID);
        }
        return containerID;
    }
    // setFocusOnIframeEventSubscriptions(needSubscribe = true)   // unsubscribe if needSubscribe == false
    // {
    //     if (this.tvWidget == null) return;
    //     const events_list: (keyof SubscribeEventsMap)[] = ['mouse_down', 'indicators_dialog', 'undo_redo_state_changed', 'onSelectedLineToolChanged', /*'legend_action',*/ 'toggle_sidebar', 'edit_object_dialog'/*, 'double_click price scale'*/];   //, 'chart_header toolbar'
    //     const method = needSubscribe ? 'subscribe' : 'unsubscribe';
    //     const widget = this.tvWidget;
    //     events_list.forEach((event) => widget[method](event, () => { this.setFocus(); })); // for accWidget and lookupDropdown form close when click on iframe
    // }
    subscribeTvWidgetEvents() {
        this.subscribeTVTimeIntervalChanged();
        this.subscribeTvSymbolChanged();
        this.subscribeUndoRedoStateChanged();
        this.subscribeMouseEvents();
    }
    unsubscribeTvWidgetEvents() {
        this.unsubscribeTVTimeIntervalChanged();
        this.unsubscribeTvSymbolChanged();
        this.unsubscribeUndoRedoStateChanged();
        this.unsubscribeMouseEvents();
    }
    subscribeTVTimeIntervalChanged() {
        if (this.tvWidget == null)
            return;
        this.tvWidget
            .activeChart()
            .onIntervalChanged()
            .subscribe(null, (interval, timeframeObj) => {
            this.onIntervalChanged(interval, timeframeObj);
        });
    }
    unsubscribeTVTimeIntervalChanged() {
        if (this.tvWidget == null)
            return;
        this.tvWidget
            .activeChart()
            .onIntervalChanged()
            .unsubscribe(null, (interval, timeframeObj) => {
            this.onIntervalChanged(interval, timeframeObj);
        });
    }
    onIntervalChanged(interval, timeframeObj) {
        this.updatePanelHeader();
    }
    subscribeTvSymbolChanged() {
        if (this.tvWidget == null)
            return;
        this.tvWidget
            .activeChart()
            .onSymbolChanged()
            .subscribe(null, () => {
            this.onTvSymbolChanged();
        });
    }
    unsubscribeTvSymbolChanged() {
        if (this.tvWidget == null)
            return;
        this.tvWidget
            .activeChart()
            .onSymbolChanged()
            .unsubscribe(null, () => {
            this.onTvSymbolChanged();
        });
    }
    onTvSymbolChanged() {
        this.updatePanelHeader();
    }
    subscribeUndoRedoStateChanged() {
        if (this.tvWidget == null)
            return;
        this.tvWidget.subscribe('undo_redo_state_changed', this.onUndoRedoStateChanged);
    }
    unsubscribeUndoRedoStateChanged() {
        if (this.tvWidget == null)
            return;
        this.tvWidget.unsubscribe('undo_redo_state_changed', this.onUndoRedoStateChanged);
    }
    subscribeMouseEvents() {
        if (this.tvWidget == null)
            return;
        this.tvWidget.subscribe('mouse_down', this.onTvChartMouseDown);
        this.tvWidget.subscribe('mouse_up', this.onTvChartMouseUp);
    }
    unsubscribeMouseEvents() {
        if (this.tvWidget == null)
            return;
        this.tvWidget.unsubscribe('mouse_down', this.onTvChartMouseDown);
        this.tvWidget.unsubscribe('mouse_up', this.onTvChartMouseUp);
    }
    onUndoRedoStateChanged(state) {
        if (!this.tVWChartReady || this.tvWidget == null)
            return;
        const symbolFromChart = this.tvWidget.activeChart().symbol();
        const symbolFromLookup = this.getTVSymbol();
        if (symbolFromChart !== symbolFromLookup) {
            // какая-то странная ситуация, когда делаешь Undo - то TV чарт возвращает InteriorID
            // для Redo - возвращает имя инструмента, поэтому такой двойственный поиск
            let instrument = DataCache.getInstrumentByName(symbolFromChart);
            if (instrument == null) {
                instrument = this.tvDataFeed.getSymbolByName(symbolFromChart);
            }
            if (instrument == null)
                return;
            this.skipApplyInstrument = true; // update only in lookup
            this.SetInstrument(instrument);
        }
    }
    Properties() {
        const props = super.Properties();
        try {
            let TVProps = '';
            if (!isNullOrUndefined(this.tvWidget) && this.tVWChartReady) {
                this.tvWidget.save((obj) => (TVProps = JSON.stringify(obj)));
            }
            if (TVProps != null) {
                props.push(new DynProperty('TradingViewProperties', TVProps, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
            }
        }
        catch (e) {
            /*TODO*/
        }
        props.push(new DynProperty('TradingViewInitialSettings', this.tvSettingsAdapter.initialSettings, DynProperty.UNKNOWN, DynProperty.HIDDEN_GROUP));
        // props.push(new DynProperty("theme", this.theme, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        const instrument = this.GetInstrument();
        if (instrument != null) {
            props.push(new DynProperty('symbol', instrument.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        return props;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        const tvWidgetInitialSettings = DynProperty.getPropValue(newProperties, 'TradingViewInitialSettings');
        if (tvWidgetInitialSettings != null) {
            this.tvSettingsAdapter.initialSettings = tvWidgetInitialSettings;
        }
        const tvWidgetPropsStr = DynProperty.getPropValue(newProperties, 'TradingViewProperties');
        if (isValidString(tvWidgetPropsStr)) {
            try {
                const tvWidgetProps = JSON.parse(tvWidgetPropsStr);
                if (!isNullOrUndefined(this.tvWidget)) {
                    this.tvWidget.load(tvWidgetProps);
                }
                else {
                    this.tvWidgetDefferedProps = tvWidgetProps;
                }
            }
            catch (e) {
                console.log('Error parsing TradingView properties');
            }
        }
        // const theme = DynProperty.getPropValue(newProperties, "theme");
        // if (theme)
        //     this.theme = theme;
        this.SetInstrument(super.getCallBackInstrument(newProperties, 'symbol'));
    }
    onTvChartMouseEnter() {
        this.isMouseInsideChart = true;
        this.validateMousePressed();
    }
    onTvChartMouseLeave() {
        this.isMouseInsideChart = false;
        this.validateMousePressed();
    }
    handleGlobalMouseDown() {
        this.isMousePressedOutside = !this.isMouseInsideChart;
        this.validateMousePressed();
    }
    handleGlobalMouseUp() {
        this.isMousePressedOutside = false;
        this.validateMousePressed();
    }
    onTvChartMouseDown(params) {
        this.isMousePressedOutside = false;
        this.validateMousePressed();
        const container = document.getElementById(this.getWidgetContainerID());
        if (container != null) {
            const evt = new Event('mousedown', { bubbles: true, cancelable: true });
            container.dispatchEvent(evt);
        }
    }
    onTvChartMouseUp(params) {
        this.isMousePressedOutside = false;
        this.validateMousePressed();
        const container = document.getElementById(this.getWidgetContainerID());
        if (container != null) {
            const evt = new Event('mouseup', { bubbles: true, cancelable: true });
            container.dispatchEvent(evt);
        }
    }
    validateMousePressed() {
        const prveDisablePointerEvents = this.get('disablePointerEvents');
        const disablePointerEvents = this.isMousePressedOutside;
        if (disablePointerEvents !== prveDisablePointerEvents) {
            void this.set('disablePointerEvents', disablePointerEvents);
        }
    }
    setWatermarkVisibility() {
        var _a, _b, _c;
        try {
            (_c = (_b = (_a = this.tvWidget) === null || _a === void 0 ? void 0 : _a.watermark()) === null || _b === void 0 ? void 0 : _b.visibility()) === null || _c === void 0 ? void 0 : _c.setValue(true, true);
        }
        catch (e) {
            /*TODO*/
        }
    }
    applyOverrides() {
        if (this.tvWidget == null) {
            return;
        }
        const OVERRIDES = {
            'paneProperties.backgroundType': 'solid',
            'paneProperties.background': ThemeManager.colors.chartBackground,
            'mainSeriesProperties.style': 1, // candle chart style
            'mainSeriesProperties.candleStyle.borderUpColor': ThemeManager.colors.Chart_BarsUpBorderColor,
            'mainSeriesProperties.candleStyle.upColor': ThemeManager.colors.Chart_BarsUpColor,
            'mainSeriesProperties.candleStyle.wickUpColor': ThemeManager.colors.Chart_WickUpBorderColor,
            'mainSeriesProperties.candleStyle.borderDownColor': ThemeManager.colors.Chart_BarsDownBorderColor,
            'mainSeriesProperties.candleStyle.downColor': ThemeManager.colors.Chart_BarsDownColor,
            'mainSeriesProperties.candleStyle.wickDownColor': ThemeManager.colors.Chart_WickDownBorderColor,
            // Main series price line
            'mainSeriesProperties.priceLineColor': ThemeManager.colors.Chart_BarsUpColor,
            'mainSeriesProperties.priceLineWidth': 1,
            'scalesProperties.showSymbolLabels': false, // is need show instrument's name near price scale
            'scalesProperties.fontSize': 12,
            'scalesProperties.textColor': ThemeManager.colors.Chart_ScalesTextColor,
            'paneProperties.vertGridProperties.color': ThemeManager.colors.chartGridColor,
            'paneProperties.horzGridProperties.color': ThemeManager.colors.chartGridColor
        };
        this.tvWidget.applyOverrides(OVERRIDES);
    }
    getTvTheme() {
        if (ThemeManager.themeValue === Themes.Light) {
            return 'light';
        }
        else {
            return 'dark';
        }
    }
}
ApplicationPanelWithTable.extendWith(TradingViewChartPanel, {
    data: function () {
        return {
            widgetContainerID: null, // id of div to set TradingView widget in
            isSymbolLinkShow: true,
            instrumentItem: null,
            disablePointerEvents: false
        };
    },
    partials: { bodyPartial: TradingViewChartPanelTemplate }
});
